/**
 * Upload Queue Manager - Phase 2.2: Deferred Upload Architecture
 * 
 * Manages deferred uploads for scene recordings with:
 * - Queue management
 * - Progress tracking
 * - Auto-retry with exponential backoff
 * - Manual retry support
 * - Status updates
 */

import logger from '../utils/logger';
import { SceneUploadStatus } from '../../../types/scene';

export interface UploadQueueEntry {
  id: string;                        // UUID
  sceneId: string;                   // Associated scene
  videoGuid: string;                 // Video identifier
  projectId: string;                 // Parent project
  status: SceneUploadStatus;
  retryCount: number;                // 0-10
  maxRetries: number;                // Default 10
  priority: 'high' | 'normal' | 'low';
  createdAt: number;
  startedAt?: number;
  completedAt?: number;
  lastError?: string;
  chunks: {                          // Chunk tracking
    total: number;
    uploaded: number;
    failed: number;
  };
  progress: number;                  // 0-100 percentage
}

type ProgressCallback = (queueId: string, progress: number, status: SceneUploadStatus) => void;

export class UploadQueueManager {
  private queue: Map<string, UploadQueueEntry> = new Map();
  private progressCallbacks: Set<ProgressCallback> = new Set();
  private uploadWorkerRef: Map<string, Worker> = new Map(); // videoGuid -> Worker

  constructor() {
    logger.info('[UploadQueueManager] Service initialized');
  }

  /**
   * Add a scene recording to the upload queue
   */
  async addToQueue(
    sceneId: string,
    videoGuid: string,
    projectId: string,
    totalChunks: number
  ): Promise<string> {
    const queueId = crypto.randomUUID();
    
    const entry: UploadQueueEntry = {
      id: queueId,
      sceneId,
      videoGuid,
      projectId,
      status: 'not_started',
      retryCount: 0,
      maxRetries: 10,
      priority: 'normal',
      createdAt: Date.now(),
      chunks: {
        total: totalChunks,
        uploaded: 0,
        failed: 0,
      },
      progress: 0,
    };

    this.queue.set(queueId, entry);
    logger.info(`[UploadQueueManager] Added to queue: ${queueId}`, {
      sceneId,
      videoGuid,
      totalChunks,
    });

    return queueId;
  }

  /**
   * Start upload for a queued entry
   * This will be implemented in Phase 1.3
   */
  async startUpload(queueId: string, uploadWorker?: Worker): Promise<void> {
    const entry = this.queue.get(queueId);
    if (!entry) {
      throw new Error(`Queue entry not found: ${queueId}`);
    }

    if (entry.status === 'uploading') {
      logger.warn(`[UploadQueueManager] Upload already in progress: ${queueId}`);
      return;
    }

    // Update status
    entry.status = 'uploading';
    entry.startedAt = Date.now();
    this.queue.set(queueId, entry);

    logger.info(`[UploadQueueManager] Starting upload: ${queueId}`, {
      videoGuid: entry.videoGuid,
      retryCount: entry.retryCount,
    });

    // Store worker reference if provided
    if (uploadWorker) {
      this.uploadWorkerRef.set(entry.videoGuid, uploadWorker);
    }

    // Notify progress callbacks
    this.notifyProgress(queueId, 0, 'uploading');
  }

  /**
   * Update upload progress
   */
  updateProgress(
    queueId: string,
    uploadedChunks: number,
    totalChunks?: number
  ): void {
    const entry = this.queue.get(queueId);
    if (!entry) {
      logger.warn(`[UploadQueueManager] Entry not found for progress update: ${queueId}`);
      return;
    }

    if (totalChunks) {
      entry.chunks.total = totalChunks;
    }

    entry.chunks.uploaded = uploadedChunks;
    entry.progress = Math.round((uploadedChunks / entry.chunks.total) * 100);
    this.queue.set(queueId, entry);

    logger.info(`[UploadQueueManager] Progress update: ${queueId}`, {
      progress: entry.progress,
      uploadedChunks,
      totalChunks: entry.chunks.total,
    });

    this.notifyProgress(queueId, entry.progress, entry.status);
  }

  /**
   * Mark upload as successful
   */
  markSuccess(queueId: string): void {
    const entry = this.queue.get(queueId);
    if (!entry) {
      logger.warn(`[UploadQueueManager] Entry not found for success: ${queueId}`);
      return;
    }

    entry.status = 'upload_success';
    entry.completedAt = Date.now();
    entry.progress = 100;
    this.queue.set(queueId, entry);

    logger.info(`[UploadQueueManager] Upload successful: ${queueId}`, {
      duration: entry.completedAt - (entry.startedAt || entry.createdAt),
    });

    this.notifyProgress(queueId, 100, 'upload_success');

    // Cleanup worker
    this.uploadWorkerRef.delete(entry.videoGuid);
  }

  /**
   * Mark upload as failed
   */
  markFailed(queueId: string, error: string): void {
    const entry = this.queue.get(queueId);
    if (!entry) {
      logger.warn(`[UploadQueueManager] Entry not found for failure: ${queueId}`);
      return;
    }

    entry.status = 'upload_failed';
    entry.lastError = error;
    this.queue.set(queueId, entry);

    logger.error(`[UploadQueueManager] Upload failed: ${queueId}`, {
      error,
      retryCount: entry.retryCount,
    });

    this.notifyProgress(queueId, entry.progress, 'upload_failed');
  }

  /**
   * Retry upload with exponential backoff
   * Returns true if retry attempt started, false if max retries exceeded
   */
  async autoRetry(queueId: string): Promise<boolean> {
    const entry = this.queue.get(queueId);
    if (!entry) {
      throw new Error(`Queue entry not found: ${queueId}`);
    }

    if (entry.retryCount >= entry.maxRetries) {
      logger.warn(`[UploadQueueManager] Max retries exceeded: ${queueId}`, {
        retryCount: entry.retryCount,
        maxRetries: entry.maxRetries,
      });
      this.markFailed(queueId, `Max retries (${entry.maxRetries}) exceeded`);
      return false;
    }

    // Exponential backoff: 2s, 4s, 8s, 16s, 32s, 60s, 60s, 60s, 60s, 60s
    const delays = [2000, 4000, 8000, 16000, 32000, 60000, 60000, 60000, 60000, 60000];
    const delay = delays[entry.retryCount] || 60000;

    entry.retryCount++;
    this.queue.set(queueId, entry);

    logger.info(`[UploadQueueManager] Auto-retry scheduled: ${queueId}`, {
      retryCount: entry.retryCount,
      delay,
    });

    // Wait for backoff delay
    await new Promise(resolve => setTimeout(resolve, delay));

    // Retry the upload (this will be implemented in Phase 1.3)
    await this.startUpload(queueId);
    return true;
  }

  /**
   * Manual retry (resets retry count)
   */
  async retryUpload(queueId: string, force: boolean = false): Promise<void> {
    const entry = this.queue.get(queueId);
    if (!entry) {
      throw new Error(`Queue entry not found: ${queueId}`);
    }

    if (force) {
      // Reset retry count on manual retry
      entry.retryCount = 0;
      entry.lastError = undefined;
    }

    logger.info(`[UploadQueueManager] Manual retry: ${queueId}`, {
      force,
      retryCount: entry.retryCount,
    });

    this.queue.set(queueId, entry);
    await this.startUpload(queueId);
  }

  /**
   * Cancel an upload
   */
  async cancelUpload(queueId: string): Promise<void> {
    const entry = this.queue.get(queueId);
    if (!entry) {
      throw new Error(`Queue entry not found: ${queueId}`);
    }

    // Abort the worker if it exists
    const worker = this.uploadWorkerRef.get(entry.videoGuid);
    if (worker) {
      worker.postMessage({ type: 'abort-upload', videoGuid: entry.videoGuid });
      worker.terminate();
      this.uploadWorkerRef.delete(entry.videoGuid);
    }

    this.queue.delete(queueId);
    logger.info(`[UploadQueueManager] Upload cancelled: ${queueId}`);
  }

  /**
   * Get upload status
   */
  getUploadStatus(queueId: string): UploadQueueEntry | undefined {
    return this.queue.get(queueId);
  }

  /**
   * Get upload status by videoGuid
   */
  getUploadStatusByVideoGuid(videoGuid: string): UploadQueueEntry | undefined {
    const entries = Array.from(this.queue.values());
    for (const entry of entries) {
      if (entry.videoGuid === videoGuid) {
        return entry;
      }
    }
    return undefined;
  }

  /**
   * Subscribe to progress updates
   */
  onProgress(callback: ProgressCallback): () => void {
    this.progressCallbacks.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.progressCallbacks.delete(callback);
    };
  }

  /**
   * Notify all progress callbacks
   */
  private notifyProgress(queueId: string, progress: number, status: SceneUploadStatus): void {
    this.progressCallbacks.forEach(callback => {
      try {
        callback(queueId, progress, status);
      } catch (error) {
        logger.error('[UploadQueueManager] Error in progress callback:', error);
      }
    });
  }

  /**
   * Get all failed uploads
   */
  async getFailedUploads(): Promise<UploadQueueEntry[]> {
    const failed: UploadQueueEntry[] = [];
    const entries = Array.from(this.queue.values());
    for (const entry of entries) {
      if (entry.status === 'upload_failed') {
        failed.push(entry);
      }
    }
    return failed;
  }

  /**
   * Clean up successful uploads older than 7 days
   */
  async cleanupSuccessfulUploads(): Promise<number> {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    let cleaned = 0;

    const entries = Array.from(this.queue.entries());
    for (const [queueId, entry] of entries) {
      if (
        entry.status === 'upload_success' &&
        entry.completedAt &&
        entry.completedAt < sevenDaysAgo
      ) {
        this.queue.delete(queueId);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      logger.info(`[UploadQueueManager] Cleaned ${cleaned} old successful uploads`);
    }

    return cleaned;
  }

  /**
   * Get queue statistics
   */
  getStats() {
    const stats = {
      total: this.queue.size,
      notStarted: 0,
      uploading: 0,
      success: 0,
      failed: 0,
    };

    const entries = Array.from(this.queue.values());
    for (const entry of entries) {
      switch (entry.status) {
        case 'not_started':
          stats.notStarted++;
          break;
        case 'uploading':
          stats.uploading++;
          break;
        case 'upload_success':
          stats.success++;
          break;
        case 'upload_failed':
          stats.failed++;
          break;
      }
    }

    return stats;
  }
}

// Export singleton instance
export const uploadQueueManager = new UploadQueueManager();
